import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Loader2, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ApiError } from "@/lib/api";
import { useCreateTeam } from "@/lib/api/teams.queries";
import { useActiveTeam } from "@/lib/team-context";

const NameSchema = z
  .string()
  .min(2, "name_too_short")
  .max(100, "name_too_long");
const SlugSchema = z
  .string()
  .regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, "slug_format")
  .max(63, "slug_too_long");

type Props = {
  /** Optional explicit trigger element (renders inside DialogTrigger). */
  trigger?: ReactNode;
  /** Controlled open prop — pass with onOpenChange for parent-managed flow. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
};

function deriveSlug(name: string): string {
  return name
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-{2,}/g, "-")
    .slice(0, 63);
}

/**
 * Creates a team. Auto-derives slug from name until the user edits the slug
 * field directly (after which we stop overwriting it).
 *
 * On success: persists the new team as the active team and continues the
 * guided flow into `/companies/new` — a fresh team has zero companies, so
 * enrolling the first NIT is always the next step (PF-342).
 */
export function CreateTeamDialog({ trigger, open, onOpenChange }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setActiveTeamId } = useActiveTeam();
  const createTeam = useCreateTeam();

  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = open !== undefined;
  const isOpen = isControlled ? open : internalOpen;
  const setOpen = (next: boolean) => {
    if (isControlled) onOpenChange?.(next);
    else setInternalOpen(next);
  };

  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [errors, setErrors] = useState<{ name?: string; slug?: string }>({});

  // Reset form whenever dialog re-opens.
  useEffect(() => {
    if (isOpen) {
      setName("");
      setSlug("");
      setSlugTouched(false);
      setErrors({});
      createTeam.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Auto-derive slug from name until user touches it.
  useEffect(() => {
    if (!slugTouched) setSlug(deriveSlug(name));
  }, [name, slugTouched]);

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const nextErrors: { name?: string; slug?: string } = {};
    const nameRes = NameSchema.safeParse(name.trim());
    if (!nameRes.success) {
      nextErrors.name = t(`teams.create.error.${nameRes.error.issues[0]?.message ?? "name_invalid"}`);
    }
    let slugForApi: string | undefined = undefined;
    if (slugTouched && slug.trim().length > 0) {
      const slugRes = SlugSchema.safeParse(slug.trim());
      if (!slugRes.success) {
        nextErrors.slug = t(`teams.create.error.${slugRes.error.issues[0]?.message ?? "slug_invalid"}`);
      } else {
        slugForApi = slugRes.data;
      }
    }
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }
    setErrors({});

    createTeam.mutate(
      { name: name.trim(), slug: slugForApi },
      {
        onSuccess: (team) => {
          setActiveTeamId(team.id);
          toast.success(t("teams.create.toast.success", { name: team.name }));
          setOpen(false);
          void navigate({ to: "/companies/new" });
        },
        onError: (err) => {
          const message =
            err instanceof ApiError ? err.message : t("teams.create.toast.error");
          toast.error(message);
        },
      },
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={setOpen}>
      {trigger ? <DialogTrigger asChild>{trigger}</DialogTrigger> : null}
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("teams.create.title")}</DialogTitle>
          <DialogDescription>{t("teams.create.description")}</DialogDescription>
        </DialogHeader>
        <form className="flex flex-col gap-4" onSubmit={onSubmit} noValidate>
          <div className="grid gap-1.5">
            <Label htmlFor="team-name">{t("teams.create.field.name")}</Label>
            <Input
              id="team-name"
              autoFocus
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("teams.create.placeholder.name")}
            />
            {errors.name ? (
              <p className="text-xs text-destructive">{errors.name}</p>
            ) : null}
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="team-slug" className="flex items-center gap-2">
              {t("teams.create.field.slug")}
              <span className="text-xs font-normal text-muted-foreground">
                {t("teams.create.field.slug_hint")}
              </span>
            </Label>
            <Input
              id="team-slug"
              value={slug}
              onChange={(e) => {
                setSlugTouched(true);
                setSlug(e.target.value.toLowerCase());
              }}
              placeholder="despacho-perez"
              autoComplete="off"
              spellCheck={false}
            />
            {errors.slug ? (
              <p className="text-xs text-destructive">{errors.slug}</p>
            ) : null}
          </div>
          <div className="flex items-start gap-2 rounded-md border border-primary/20 bg-primary/5 p-3 text-xs text-foreground/80">
            <Sparkles className="mt-0.5 h-3.5 w-3.5 flex-none text-primary" />
            <span>{t("teams.create.hint.invite")}</span>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={createTeam.isPending}
            >
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={createTeam.isPending}>
              {createTeam.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : null}
              {t("teams.create.cta")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
